import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Building2, Briefcase } from "lucide-react";
import { AnimatedSection } from "@/components/ui/AnimatedSection";

interface LinkItem {
  title: string;
  slug: string;
  description?: string;
}

interface RelatedLinksProps {
  pillarSlug?: string;
  pillarTitle?: string;
  moneyPages?: LinkItem[];
  industryPages?: LinkItem[];
  insightPages?: LinkItem[];
  currentSlug?: string;
}

export function RelatedLinks({
  pillarSlug,
  pillarTitle,
  moneyPages = [],
  industryPages = [],
  insightPages = [],
  currentSlug,
}: RelatedLinksProps) {
  // Filter out current page
  const filteredMoneyPages = moneyPages.filter(page => page.slug !== currentSlug);
  const filteredIndustryPages = industryPages.filter(page => page.slug !== currentSlug);
  const filteredInsightPages = insightPages.filter(page => page.slug !== currentSlug);

  const hasLinks = filteredMoneyPages.length > 0 || filteredIndustryPages.length > 0 || filteredInsightPages.length > 0;

  if (!hasLinks && !pillarSlug) return null;

  return (
    <section className="section-spacing bg-secondary/30">
      <div className="container-wide">
        <AnimatedSection className="text-center mb-12">
          <h2 className="heading-section mb-4">Related Resources</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore more about our services and insights
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Back to Pillar */}
          {pillarSlug && pillarTitle && (
            <AnimatedSection delay={0}>
              <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20 h-full">
                <div className="flex items-center gap-2 text-primary mb-4">
                  <Briefcase className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Service Pillar</span>
                </div>
                <Link 
                  to={`/services/${pillarSlug}`}
                  className="group block"
                >
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {pillarTitle}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Explore our complete {pillarTitle} services
                  </p>
                  <span className="inline-flex items-center text-primary text-sm font-medium">
                    Learn More <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>
            </AnimatedSection>
          )}

          {/* Related Services (Money Pages) */}
          {filteredMoneyPages.length > 0 && (
            <AnimatedSection delay={0.1}>
              <div className="p-6 rounded-2xl bg-secondary/50 border border-border h-full">
                <div className="flex items-center gap-2 text-foreground mb-4">
                  <Briefcase className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Related Services</span>
                </div>
                <ul className="space-y-3">
                  {filteredMoneyPages.slice(0, 4).map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={`/services/${pillarSlug}/${page.slug}`}
                        className="group flex items-start gap-2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowRight className="w-4 h-4 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm">{page.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          )}

          {/* Industry Pages */}
          {filteredIndustryPages.length > 0 && (
            <AnimatedSection delay={0.2}>
              <div className="p-6 rounded-2xl bg-secondary/50 border border-border h-full">
                <div className="flex items-center gap-2 text-foreground mb-4">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Industries</span>
                </div>
                <ul className="space-y-3">
                  {filteredIndustryPages.slice(0, 4).map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={`/industries/${page.slug}`}
                        className="group flex items-start gap-2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowRight className="w-4 h-4 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm">{page.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          )}

          {/* Insight Pages */}
          {filteredInsightPages.length > 0 && (
            <AnimatedSection delay={0.3}>
              <div className="p-6 rounded-2xl bg-secondary/50 border border-border h-full">
                <div className="flex items-center gap-2 text-foreground mb-4">
                  <BookOpen className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Insights</span>
                </div>
                <ul className="space-y-3">
                  {filteredInsightPages.slice(0, 4).map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={`/insights/${page.slug}`}
                        className="group flex items-start gap-2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowRight className="w-4 h-4 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm">{page.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          )}
        </div>
      </div>
    </section>
  );
}
